import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Box,
} from '@mui/material';

const BLOG_CATEGORIES = [
  'Direct Tax',
  'Indirect Tax',
  'International Tax',
  'Zakat',
  'Regulatory',
  'Compliance',
  'Government Incentives',
  'Corporate Tax',
];

const BlogDialog = ({ open, blog, userName, onClose, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    excerpt: '',
    content: '',
    imageUrl: '',
    author: userName || '',
    category: '',
    linkedinUrl: '',
    betaVersion: false,
    metaKeywords: '',
  });

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        subtitle: blog.subtitle || '',
        description: blog.description || '',
        excerpt: blog.excerpt || '',
        content: blog.content || '',
        imageUrl: blog.imageUrl || '',
        author: blog.author || '',
        category: blog.category || '',
        linkedinUrl: blog.linkedinUrl || '',
        betaVersion: blog.betaVersion || false,
        metaKeywords: Array.isArray(blog.metaKeywords)
          ? blog.metaKeywords.join(', ')
          : '',
      });
    } else {
      setFormData({
        title: '',
        subtitle: '',
        description: '',
        excerpt: '',
        content: '',
        imageUrl: '',
        author: userName || '',
        category: '',
        linkedinUrl: '',
        betaVersion: false,
        metaKeywords: '',
      });
    }
  }, [blog, userName, open]);

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleSwitchChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.checked,
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        metaKeywords: formData.metaKeywords
          ? formData.metaKeywords
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
      };
      await onSubmit(payload);
    } catch (error) {
      console.error('Error submitting blog:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {blog ? 'Edit' : 'Create New'} Blog Post
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={handleChange('title')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subtitle"
                value={formData.subtitle}
                onChange={handleChange('subtitle')}
                helperText="Short subtitle for the blog"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange('description')}
                required
                helperText="Full description of the blog content"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Excerpt"
                multiline
                rows={3}
                value={formData.excerpt}
                onChange={handleChange('excerpt')}
                helperText="Short excerpt for preview"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                multiline
                rows={6}
                value={formData.content}
                onChange={handleChange('content')}
                helperText="Main blog content (markdown supported)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={formData.imageUrl}
                onChange={handleChange('imageUrl')}
                helperText="URL to the blog thumbnail image"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Author"
                value={formData.author}
                onChange={handleChange('author')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category}
                onChange={handleChange('category')}
                required
              >
                {BLOG_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="LinkedIn URL"
                value={formData.linkedinUrl}
                onChange={handleChange('linkedinUrl')}
                helperText="Link to LinkedIn post about this blog"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Keywords (comma separated)"
                value={formData.metaKeywords}
                onChange={handleChange('metaKeywords')}
                helperText="Use keywords for SEO discovery"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.betaVersion}
                    onChange={handleSwitchChange('betaVersion')}
                  />
                }
                label="Beta Version"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
          variant="outlined"
          sx={{
            borderColor: '#232536',
            color: '#232536',
            textTransform: 'none',
            '&:hover': { borderColor: '#3a3e5a', color: '#3a3e5a' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            bgcolor: '#232536',
            color: 'white',
            '&:hover': { bgcolor: '#1e2130' },
          }}
        >
          {submitting ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : blog ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogDialog;
